import { SupabaseClient } from "@supabase/supabase-js";
import { EventVenueCandidate } from "../database/venues";

/**
 * Gets an event venue candidate by its ID.
 *
 * @param supabase - The Supabase client
 * @param eventVenueCandidateId - The ID of the event venue candidate
 * @returns The event venue candidate or null if not found
 * @throws An error if there is an issue fetching the event venue candidate
 */
export async function getEventVenueCandidateById(
  supabase: SupabaseClient,
  eventVenueCandidateId: number
): Promise<EventVenueCandidate | null> {
  const { data, error } = await supabase
    .schema("nowadays")
    .from("event_venue_candidate")
    .select("*")
    .eq("event_venue_candidate_id", eventVenueCandidateId)
    .maybeSingle();

  if (error) {
    throw new Error(`Error fetching event venue candidate ${eventVenueCandidateId}: ${error.message}`);
  }

  return data as EventVenueCandidate | null;
}

/**
 * Gets an event venue candidate by its venue and event ID.
 *
 * @param supabase - The Supabase client
 * @param venueStaticId - The ID of the venue
 * @param eventId - The ID of the event
 * @returns The event venue candidate or null if not found
 * @throws An error if there is an issue fetching the event venue candidate
 */
export async function getEventVenueCandidateByVenueAndEventId(
  supabase: SupabaseClient,
  venueStaticId: number,
  eventId: number
): Promise<EventVenueCandidate | null> {
  const { data, error } = await supabase
    .schema("nowadays")
    .from("event_venue_candidate")
    .select("*")
    .eq("venue_static_id", venueStaticId)
    .eq("event_id", eventId)
    .maybeSingle();

  if (error) {
    throw new Error(
      `Error fetching event venue candidate by venue and event id ${venueStaticId} and ${eventId}: ${error.message}`
    );
  }

  return data as EventVenueCandidate | null;
}

/**
 * Merges a patch into a candidate's `internal_candidate_notes` JSON-text
 * column, preserving any other keys already stored (e.g. `tracker_notes`).
 * Shared by every write path that needs to set a key on this column (e.g.
 * `manual_pipeline_status`) without clobbering the rest of the blob.
 *
 * @param existingNotesRaw - The candidate's current `internal_candidate_notes`
 *   value, or null for a fresh candidate.
 * @param patch - Keys to merge in; overwrites any existing key of the same name.
 * @returns The merged, JSON-stringified notes.
 */
export function mergeCandidateNotes(existingNotesRaw: string | null, patch: Record<string, unknown>): string {
  let notes: Record<string, unknown> = {};
  try {
    if (existingNotesRaw) {
      const parsed: unknown = JSON.parse(existingNotesRaw);
      if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
        notes = parsed as Record<string, unknown>;
      }
    }
  } catch {
    notes = {};
  }
  return JSON.stringify({ ...notes, ...patch });
}
